import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Upload,
  FileText,
  ArrowLeft,
  Download,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  FileSpreadsheet,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Eye,
  Info,
  ChevronDown,
  ChevronRight,
  Trash2,
  History,
  Filter,
  Pencil,
  MessageSquare,
  Save,
  X
} from "lucide-react";
import { toast } from "sonner";
import { useDropzone } from "react-dropzone";

export default function BudgetComparisonPage() {
  const { budgetId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  
  const [budget, setBudget] = useState(null);
  const [property, setProperty] = useState(null);
  const [propertyId, setPropertyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [comparison, setComparison] = useState(null);
  const [previousComparisons, setPreviousComparisons] = useState([]);
  const [activeTab, setActiveTab] = useState("upload");
  
  // Preview state for new extraction flow
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [excludedExpenses, setExcludedExpenses] = useState(new Set());
  const [excludedRevenues, setExcludedRevenues] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [editingNotes, setEditingNotes] = useState(null); // comparison ID being edited
  const [notesText, setNotesText] = useState("");

  useEffect(() => {
    fetchBudgetData();
    fetchPreviousComparisons();
  }, [budgetId]);

  useEffect(() => {
    if (previousComparisons.length > 0 && !comparison && !preview) {
      setActiveTab("history");
    }
  }, [previousComparisons]);

  const backUrl = propertyId ? `/properties/${propertyId}` : (property?.id ? `/properties/${property.id}` : (budget?.property_id ? `/properties/${budget.property_id}` : '/dashboard'));

  const fetchBudgetData = async () => {
    try {
      const response = await axios.get(`${API}/budgets/${budgetId}`, {
        headers: getAuthHeader()
      });
      setBudget(response.data);
      const propId = response.data.property_id;
      setPropertyId(propId);
      
      const propResponse = await axios.get(`${API}/properties/${response.data.property_id}`, {
        headers: getAuthHeader()
      });
      setProperty(propResponse.data);
    } catch (error) {
      toast.error("Failed to load budget data");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviousComparisons = async () => {
    try {
      const response = await axios.get(`${API}/budgets/${budgetId}/comparisons`, {
        headers: getAuthHeader()
      });
      setPreviousComparisons(response.data.comparisons || []);
    } catch (error) {
      console.error("Failed to fetch comparisons:", error);
    }
  };

  const pollComparisonStatus = useCallback(async (comparisonId) => {
    const maxAttempts = 60;
    let attempts = 0;
    
    const poll = async () => {
      try {
        const response = await axios.get(
          `${API}/budgets/${budgetId}/comparisons/${comparisonId}/status`,
          { headers: getAuthHeader() }
        );
        
        const { status, progress, error } = response.data;
        setUploadProgress(progress || 0);
        
        if (status === "completed") {
          const fullData = await axios.get(
            `${API}/budgets/${budgetId}/comparisons/${comparisonId}`,
            { headers: getAuthHeader() }
          );
          setComparison({
            comparison_id: comparisonId,
            comparison: fullData.data.comparison_result
          });
          setActiveTab("results");
          fetchPreviousComparisons();
          setUploading(false);
          setUploadProgress(100);
          setPreview(null);
          toast.success("Budget comparison complete!");
          return;
        } else if (status === "error") {
          setUploading(false);
          setUploadProgress(0);
          toast.error(error || "Comparison failed");
          return;
        }
        
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setUploading(false);
          setUploadProgress(0);
          toast.error("Comparison timed out. Check History tab.");
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setUploading(false);
          toast.error("Failed to check comparison status");
        }
      }
    };
    
    poll();
  }, [budgetId, getAuthHeader, fetchPreviousComparisons]);

  // New: Preview extraction instead of direct comparison
  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    const formData = new FormData();
    formData.append("file", file);
    
    setPreviewLoading(true);
    setExcludedExpenses(new Set());
    setExcludedRevenues(new Set());
    
    try {
      toast.info("Extracting budget items for preview...", { duration: 10000 });
      
      const response = await axios.post(
        `${API}/budgets/${budgetId}/compare/preview`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      setPreview(response.data);
      // Expand all categories by default
      const allCategories = new Set([
        ...Object.keys(response.data.expense_categories || {}),
        ...Object.keys(response.data.revenue_categories || {})
      ]);
      setExpandedCategories(allCategories);
      setActiveTab("preview");
      toast.success("Budget items extracted! Review and run comparison.");
      
    } catch (error) {
      console.error("Preview error:", error);
      toast.error(error.response?.data?.detail || "Failed to extract budget items");
    } finally {
      setPreviewLoading(false);
    }
  }, [budgetId, getAuthHeader]);

  // Run comparison with exclusions
  const runComparisonWithExclusions = async () => {
    if (!preview?.preview_id) return;
    
    setUploading(true);
    setUploadProgress(5);
    
    try {
      const response = await axios.post(
        `${API}/budgets/${budgetId}/compare/run?preview_id=${preview.preview_id}`,
        {
          expense_indices: Array.from(excludedExpenses),
          revenue_indices: Array.from(excludedRevenues)
        },
        { headers: getAuthHeader() }
      );
      
      const { comparison_id } = response.data;
      toast.info("Processing comparison... This may take up to a minute.", { duration: 15000 });
      pollComparisonStatus(comparison_id);
      
    } catch (error) {
      console.error("Comparison error:", error);
      toast.error(error.response?.data?.detail || "Failed to run comparison");
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Legacy direct upload (fallback)
  const runDirectComparison = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    const formData = new FormData();
    formData.append("file", file);
    
    setUploading(true);
    setUploadProgress(5);
    
    try {
      toast.info("Uploading and analyzing your budget file...", { duration: 10000 });
      
      const response = await axios.post(
        `${API}/budgets/${budgetId}/compare`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            "Content-Type": "multipart/form-data"
          }
        }
      );
      
      const { comparison_id } = response.data;
      toast.info("Processing in progress... This may take up to a minute.", { duration: 15000 });
      pollComparisonStatus(comparison_id);
      
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload budget file");
      setUploading(false);
      setUploadProgress(0);
    }
  }, [budgetId, getAuthHeader, pollComparisonStatus]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: uploading || previewLoading
  });

  const toggleExpenseExclusion = (index) => {
    setExcludedExpenses(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleRevenueExclusion = (index) => {
    setExcludedRevenues(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  const handleDownloadComparison = async (comparisonId) => {
    try {
      toast.info("Preparing Excel download...");
      const response = await axios.get(
        `${API}/budgets/${budgetId}/comparisons/${comparisonId}/export-excel`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Budget_Comparison_${property?.name || 'Report'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Comparison downloaded!");
    } catch (error) {
      toast.error("Failed to download comparison");
    }
  };

  const handleExportAllComparisons = async () => {
    try {
      toast.info("Preparing all comparisons for download...");
      const response = await axios.get(
        `${API}/budgets/${budgetId}/comparisons/export-all`,
        {
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${property?.name || 'Budget'}_All_Comparisons.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("All comparisons downloaded!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to export comparisons");
    }
  };

  const handleDeleteComparison = async (comparisonId) => {
    if (!confirm("Delete this comparison?")) return;
    
    try {
      await axios.delete(
        `${API}/budgets/${budgetId}/comparisons/${comparisonId}`,
        { headers: getAuthHeader() }
      );
      toast.success("Comparison deleted");
      fetchPreviousComparisons();
      if (comparison?.comparison_id === comparisonId) {
        setComparison(null);
        setActiveTab("upload");
      }
    } catch (error) {
      toast.error("Failed to delete comparison");
    }
  };

  const handleStartEditNotes = (comp) => {
    setEditingNotes(comp.id);
    setNotesText(comp.notes || "");
  };

  const handleSaveNotes = async (comparisonId) => {
    try {
      await axios.put(
        `${API}/budgets/${budgetId}/comparisons/${comparisonId}/notes`,
        { notes: notesText },
        { headers: getAuthHeader() }
      );
      toast.success("Notes saved");
      setEditingNotes(null);
      fetchPreviousComparisons();
    } catch (error) {
      toast.error("Failed to save notes");
    }
  };

  const handleCancelEditNotes = () => {
    setEditingNotes(null);
    setNotesText("");
  };

  const loadComparison = async (comparisonId) => {
    try {
      const response = await axios.get(
        `${API}/budgets/${budgetId}/comparisons/${comparisonId}`,
        { headers: getAuthHeader() }
      );
      setComparison({
        comparison_id: response.data.id,
        uploaded_filename: response.data.uploaded_filename,
        comparison: response.data.comparison_result
      });
      setActiveTab("results");
    } catch (error) {
      toast.error("Failed to load comparison");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  // Group comparison results by category
  const groupedResults = useMemo(() => {
    if (!comparison?.comparison) return {};
    
    const matched = comparison.comparison.matched_items || [];
    const grouped = {};
    
    matched.forEach(item => {
      const cat = item.category || 'Other';
      if (!grouped[cat]) {
        grouped[cat] = {
          items: [],
          totalOur: 0,
          totalUploaded: 0,
          totalVariance: 0
        };
      }
      grouped[cat].items.push(item);
      grouped[cat].totalOur += item.our_amount || 0;
      grouped[cat].totalUploaded += item.uploaded_amount || 0;
      grouped[cat].totalVariance += item.variance || 0;
    });
    
    return grouped;
  }, [comparison]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  const compResult = comparison?.comparison || {};

  return (
    <Layout>
      <div className="space-y-6" data-testid="budget-comparison-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link to={backUrl}>
              <Button variant="ghost" size="icon" data-testid="back-button">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                Compare Budgets
              </h1>
              <p className="text-slate-500">
                {property?.name} · FY{budget?.fiscal_year_start}
              </p>
            </div>
          </div>
          {comparison && (
            <Button 
              onClick={() => handleDownloadComparison(comparison.comparison_id)}
              className="bg-emerald-600 hover:bg-emerald-700 gap-2"
            >
              <Download className="w-4 h-4" />
              Download Excel Report
            </Button>
          )}
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="upload" className="gap-2">
              <Upload className="w-4 h-4" />
              New Comparison
            </TabsTrigger>
            <TabsTrigger value="preview" className="gap-2" disabled={!preview}>
              <Eye className="w-4 h-4" />
              Preview & Exclude
            </TabsTrigger>
            <TabsTrigger value="results" className="gap-2" disabled={!comparison}>
              <TrendingUp className="w-4 h-4" />
              Results
            </TabsTrigger>
            <TabsTrigger value="history" className="gap-2">
              <History className="w-4 h-4" />
              History ({previousComparisons.length})
            </TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="upload" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Upload Section */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Upload className="w-5 h-5 text-emerald-600" />
                    Upload Your Existing Budget
                  </CardTitle>
                  <CardDescription>
                    Upload a budget file - our AI will extract items for you to review before comparing.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div
                    {...getRootProps()}
                    className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                      isDragActive 
                        ? 'border-emerald-500 bg-emerald-50' 
                        : (uploading || previewLoading)
                          ? 'border-slate-300 bg-slate-50 cursor-not-allowed'
                          : 'border-slate-300 hover:border-emerald-400 hover:bg-slate-50'
                    }`}
                  >
                    <input {...getInputProps()} />
                    
                    {previewLoading ? (
                      <div className="space-y-4">
                        <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto" />
                        <p className="text-slate-600">Extracting budget items...</p>
                        <p className="text-sm text-slate-500">
                          Our AI is analyzing your document
                        </p>
                      </div>
                    ) : uploading ? (
                      <div className="space-y-4">
                        <Loader2 className="w-12 h-12 text-emerald-600 animate-spin mx-auto" />
                        <p className="text-slate-600">Running comparison...</p>
                        <Progress value={uploadProgress} className="w-full max-w-xs mx-auto" />
                        <p className="text-sm text-slate-500">
                          {uploadProgress < 30 ? "Calculating budget totals..." : 
                           uploadProgress < 60 ? "Matching expense items..." :
                           uploadProgress < 90 ? "Calculating variances..." : "Finalizing comparison..."}
                        </p>
                      </div>
                    ) : (
                      <>
                        <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                        {isDragActive ? (
                          <p className="text-emerald-600 font-medium">Drop your budget file here...</p>
                        ) : (
                          <>
                            <p className="text-slate-600 mb-2">
                              Drag & drop your budget file here, or click to browse
                            </p>
                            <p className="text-sm text-slate-400">
                              Supports PDF, Excel (.xlsx, .xls), Word (.doc, .docx), CSV
                            </p>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* How It Works */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Info className="w-5 h-5 text-blue-600" />
                    How It Works
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">Extraction</h4>
                      <p className="text-sm text-slate-500">
                        Our system reads your uploaded budget and extracts all expense items with their amounts.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">Smart Matching</h4>
                      <p className="text-sm text-slate-500">
                        Items are intelligently matched even with different names (e.g., CC Commission = Credit Card Fees).
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">Variance Analysis</h4>
                      <p className="text-sm text-slate-500">
                        See exactly where budgets differ - amounts, missing items, and extra items.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-700 font-semibold text-sm flex-shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">Excel Report</h4>
                      <p className="text-sm text-slate-500">
                        Download a professional comparison report with highlights and recommendations.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Preview Tab - Review extracted items before comparison */}
          <TabsContent value="preview" className="mt-6">
            {preview && (
              <div className="space-y-6">
                {/* Preview Header */}
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-emerald-600" />
                        <div>
                          <h3 className="font-semibold text-emerald-900">{preview.filename}</h3>
                          <p className="text-sm text-emerald-700">
                            {preview.expenses?.length || 0} expenses · {preview.revenues?.length || 0} revenue items extracted
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-white">
                          <Filter className="w-3 h-3 mr-1" />
                          {excludedExpenses.size + excludedRevenues.size} excluded
                        </Badge>
                        <Button
                          onClick={runComparisonWithExclusions}
                          disabled={uploading}
                          className="bg-emerald-600 hover:bg-emerald-700"
                        >
                          {uploading ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Run Comparison
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    {uploading && (
                      <Progress value={uploadProgress} className="mt-4" />
                    )}
                  </CardContent>
                </Card>

                {/* Info Banner */}
                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-blue-800">
                      <strong>Review the extracted items below.</strong> Uncheck any items you want to exclude from the comparison 
                      (like subtotals that were incorrectly extracted). Then click &quot;Run Comparison&quot; to continue.
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Expenses by Category */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <TrendingDown className="w-5 h-5 text-red-500" />
                          Expenses
                        </span>
                        <Badge variant="outline">
                          {(preview.expenses?.length || 0) - excludedExpenses.size} items
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[500px] pr-4">
                        <div className="space-y-3">
                          {Object.entries(preview.expense_categories || {}).map(([category, items]) => (
                            <Collapsible
                              key={category}
                              open={expandedCategories.has(category)}
                              onOpenChange={() => toggleCategory(category)}
                            >
                              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                                <span className="font-medium text-slate-700">{category}</span>
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="text-xs">
                                    {items.length} items
                                  </Badge>
                                  {expandedCategories.has(category) ? (
                                    <ChevronDown className="w-4 h-4 text-slate-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-slate-500" />
                                  )}
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="mt-2 space-y-1">
                                {items.map((item) => (
                                  <div
                                    key={item.index}
                                    className={`flex items-center gap-3 p-2 rounded border ${
                                      excludedExpenses.has(item.index)
                                        ? 'bg-slate-100 border-slate-200 opacity-60'
                                        : 'bg-white border-slate-200'
                                    }`}
                                  >
                                    <Checkbox
                                      checked={!excludedExpenses.has(item.index)}
                                      onCheckedChange={() => toggleExpenseExclusion(item.index)}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm truncate ${excludedExpenses.has(item.index) ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                                        {item.name}
                                      </p>
                                    </div>
                                    <span className={`text-sm font-medium ${excludedExpenses.has(item.index) ? 'text-slate-400' : 'text-slate-900'}`}>
                                      {formatCurrency(item.annual_total)}
                                    </span>
                                  </div>
                                ))}
                              </CollapsibleContent>
                            </Collapsible>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {/* Revenue by Category */}
                  <Card className="border-slate-200">
                    <CardHeader className="pb-3">
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <TrendingUp className="w-5 h-5 text-emerald-500" />
                          Revenue
                        </span>
                        <Badge variant="outline">
                          {(preview.revenues?.length || 0) - excludedRevenues.size} items
                        </Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="h-[500px] pr-4">
                        <div className="space-y-3">
                          {Object.entries(preview.revenue_categories || {}).map(([category, items]) => (
                            <Collapsible
                              key={category}
                              open={expandedCategories.has(category)}
                              onOpenChange={() => toggleCategory(category)}
                            >
                              <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-emerald-50 rounded-lg hover:bg-emerald-100 transition-colors">
                                <span className="font-medium text-emerald-700">{category}</span>
                                <div className="flex items-center gap-2">
                                  <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700">
                                    {items.length} items
                                  </Badge>
                                  {expandedCategories.has(category) ? (
                                    <ChevronDown className="w-4 h-4 text-emerald-500" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-emerald-500" />
                                  )}
                                </div>
                              </CollapsibleTrigger>
                              <CollapsibleContent className="mt-2 space-y-1">
                                {items.map((item) => (
                                  <div
                                    key={item.index}
                                    className={`flex items-center gap-3 p-2 rounded border ${
                                      excludedRevenues.has(item.index)
                                        ? 'bg-slate-100 border-slate-200 opacity-60'
                                        : 'bg-white border-emerald-100'
                                    }`}
                                  >
                                    <Checkbox
                                      checked={!excludedRevenues.has(item.index)}
                                      onCheckedChange={() => toggleRevenueExclusion(item.index)}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm truncate ${excludedRevenues.has(item.index) ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                                        {item.name}
                                      </p>
                                    </div>
                                    <span className={`text-sm font-medium ${excludedRevenues.has(item.index) ? 'text-slate-400' : 'text-emerald-700'}`}>
                                      {formatCurrency(item.annual_total)}
                                    </span>
                                  </div>
                                ))}
                              </CollapsibleContent>
                            </Collapsible>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>

                {/* Summary */}
                <Card className="border-slate-200">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                      <div>
                        <p className="text-sm text-slate-500">Total Expenses</p>
                        <p className="text-xl font-bold text-red-600">{formatCurrency(preview.total_expenses)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Revenue</p>
                        <p className="text-xl font-bold text-emerald-600">{formatCurrency(preview.total_revenue)}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Gross Profit</p>
                        <p className={`text-xl font-bold ${preview.gross_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(preview.gross_profit)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Net Profit</p>
                        <p className={`text-xl font-bold ${preview.net_profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatCurrency(preview.net_profit)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Results Tab */}
          <TabsContent value="results" className="mt-6">
            {comparison && (
              <div className="space-y-6">
                {/* Financial Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Revenue */}
                  <Card className="border-emerald-200 bg-emerald-50">
                    <CardContent className="p-4">
                      <p className="text-xs text-emerald-600 font-medium mb-1">TOTAL REVENUE</p>
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-xs text-emerald-700">Created</p>
                          <p className="text-lg font-bold text-emerald-900">
                            {formatCurrency(compResult.our_total_revenue || compResult.profit_comparison?.our_gross_profit + compResult.total_our_budget || 0)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-emerald-700">Uploaded</p>
                          <p className="text-lg font-bold text-emerald-900">
                            {formatCurrency(compResult.uploaded_total_revenue || compResult.profit_comparison?.uploaded_gross_profit + compResult.total_uploaded_budget || 0)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Expenses */}
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-4">
                      <p className="text-xs text-red-600 font-medium mb-1">TOTAL EXPENSES</p>
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-xs text-red-700">Created</p>
                          <p className="text-lg font-bold text-red-900">
                            {formatCurrency(compResult.total_our_budget)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-red-700">Uploaded</p>
                          <p className="text-lg font-bold text-red-900">
                            {formatCurrency(compResult.total_uploaded_budget)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Gross Profit */}
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4">
                      <p className="text-xs text-blue-600 font-medium mb-1">GROSS PROFIT</p>
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-xs text-blue-700">Created</p>
                          <p className={`text-lg font-bold ${(compResult.profit_comparison?.our_gross_profit || 0) >= 0 ? 'text-blue-900' : 'text-red-600'}`}>
                            {formatCurrency(compResult.profit_comparison?.our_gross_profit || 0)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-blue-700">Uploaded</p>
                          <p className={`text-lg font-bold ${(compResult.profit_comparison?.uploaded_gross_profit || 0) >= 0 ? 'text-blue-900' : 'text-red-600'}`}>
                            {formatCurrency(compResult.profit_comparison?.uploaded_gross_profit || 0)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Net Profit */}
                  <Card className={`border-2 ${(compResult.profit_comparison?.uploaded_net_profit || 0) >= 0 ? 'border-amber-300 bg-amber-50' : 'border-red-300 bg-red-50'}`}>
                    <CardContent className="p-4">
                      <p className="text-xs text-slate-600 font-medium mb-1">NET PROFIT</p>
                      <div className="flex justify-between items-end">
                        <div>
                          <p className="text-xs text-slate-600">Created</p>
                          <p className={`text-lg font-bold ${(compResult.profit_comparison?.our_net_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                            {formatCurrency(compResult.profit_comparison?.our_net_profit || 0)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-600">Uploaded</p>
                          <p className={`text-lg font-bold ${(compResult.profit_comparison?.uploaded_net_profit || 0) >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                            {formatCurrency(compResult.profit_comparison?.uploaded_net_profit || 0)}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Expense Variance Summary */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-600">Expense Variance</p>
                      <p className={`text-2xl font-bold ${compResult.total_variance > 0 ? 'text-red-600' : compResult.total_variance < 0 ? 'text-emerald-600' : 'text-slate-900'}`}>
                        {formatCurrency(compResult.total_variance)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        {compResult.total_variance > 0 ? 'Created budget higher' : compResult.total_variance < 0 ? 'Uploaded budget higher' : 'Equal'}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-600">Items Matched</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {compResult.matched_items?.length || 0}
                      </p>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <p className="text-sm text-slate-600">Missing Items</p>
                      <p className="text-2xl font-bold text-amber-600">
                        {compResult.missing_in_uploaded?.length || 0}
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Summary Text */}
                {compResult.matching_summary && (
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <p className="text-slate-700">{compResult.matching_summary}</p>
                    </CardContent>
                  </Card>
                )}

                {/* Matched Items */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                      Matched Expense Items
                    </CardTitle>
                    <CardDescription>
                      Items found in both budgets with variance analysis
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[400px]">
                      <table className="w-full">
                        <thead className="bg-slate-100 sticky top-0">
                          <tr>
                            <th className="text-left p-3 text-sm font-medium text-slate-600">Expense Item</th>
                            <th className="text-right p-3 text-sm font-medium text-slate-600">Created Budget</th>
                            <th className="text-right p-3 text-sm font-medium text-slate-600">Uploaded</th>
                            <th className="text-right p-3 text-sm font-medium text-slate-600">Variance</th>
                            <th className="text-right p-3 text-sm font-medium text-slate-600">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {compResult.matched_items?.map((item, idx) => (
                            <tr key={idx} className={`border-b hover:bg-slate-50 ${item.variance !== 0 ? 'bg-red-50/50' : ''}`}>
                              <td className="p-3">
                                <p className="font-medium text-slate-900">{item.our_name}</p>
                                {item.our_name !== item.uploaded_name && (
                                  <p className="text-xs text-slate-500">Matched: {item.uploaded_name}</p>
                                )}
                              </td>
                              <td className="p-3 text-right font-mono text-slate-900">
                                {formatCurrency(item.our_amount)}
                              </td>
                              <td className="p-3 text-right font-mono text-slate-900">
                                {formatCurrency(item.uploaded_amount)}
                              </td>
                              <td className={`p-3 text-right font-mono font-medium ${item.variance > 0 ? 'text-red-600' : item.variance < 0 ? 'text-amber-600' : 'text-slate-500'}`}>
                                {item.variance > 0 && '+'}
                                {formatCurrency(item.variance)}
                              </td>
                              <td className={`p-3 text-right font-mono ${item.variance !== 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                {item.variance_percent ? `${item.variance_percent > 0 ? '+' : ''}${item.variance_percent.toFixed(1)}%` : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </ScrollArea>
                  </CardContent>
                </Card>

                {/* Two Column Layout for Missing Items */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Missing from Uploaded */}
                  <Card className="border-red-200">
                    <CardHeader className="bg-red-50">
                      <CardTitle className="flex items-center gap-2 text-red-700">
                        <XCircle className="w-5 h-5" />
                        Missing from Uploaded Budget
                      </CardTitle>
                      <CardDescription className="text-red-600">
                        {compResult.only_in_our_budget?.length || 0} items in Created Budget missing from uploaded budget
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ScrollArea className="h-[250px]">
                        {compResult.only_in_our_budget?.length > 0 ? (
                          <table className="w-full">
                            <thead className="bg-red-100 sticky top-0">
                              <tr>
                                <th className="text-left p-3 text-sm font-medium text-red-700">Expense</th>
                                <th className="text-right p-3 text-sm font-medium text-red-700">Created Budget</th>
                              </tr>
                            </thead>
                            <tbody>
                              {compResult.only_in_our_budget?.map((item, idx) => (
                                <tr key={idx} className="border-b border-red-100 bg-red-50">
                                  <td className="p-3">
                                    <p className="font-medium text-red-900">{item.name}</p>
                                    <p className="text-xs text-red-600">{item.category}</p>
                                  </td>
                                  <td className="p-3 text-right font-mono text-red-700 font-medium">
                                    {formatCurrency(item.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="p-6 text-center text-slate-500">
                            <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                            No missing items
                          </div>
                        )}
                      </ScrollArea>
                    </CardContent>
                  </Card>

                  {/* Extra in Uploaded */}
                  <Card className="border-amber-200">
                    <CardHeader className="bg-amber-50">
                      <CardTitle className="flex items-center gap-2 text-amber-700">
                        <AlertTriangle className="w-5 h-5" />
                        Extra in Uploaded Budget
                      </CardTitle>
                      <CardDescription className="text-amber-600">
                        {compResult.only_in_uploaded_budget?.length || 0} items in the uploaded budget that we do not have
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ScrollArea className="h-[250px]">
                        {compResult.only_in_uploaded_budget?.length > 0 ? (
                          <table className="w-full">
                            <thead className="bg-amber-100 sticky top-0">
                              <tr>
                                <th className="text-left p-3 text-sm font-medium text-amber-700">Expense</th>
                                <th className="text-right p-3 text-sm font-medium text-amber-700">Their Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {compResult.only_in_uploaded_budget?.map((item, idx) => (
                                <tr key={idx} className="border-b border-amber-100 bg-amber-50">
                                  <td className="p-3">
                                    <p className="font-medium text-amber-900">{item.name}</p>
                                    <p className="text-xs text-amber-600">{item.category}</p>
                                  </td>
                                  <td className="p-3 text-right font-mono text-amber-700 font-medium">
                                    {formatCurrency(item.amount)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        ) : (
                          <div className="p-6 text-center text-slate-500">
                            <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
                            No extra items
                          </div>
                        )}
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6">
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Previous Comparisons</CardTitle>
                    <CardDescription>
                      View, download, or compare against additional budgets
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {previousComparisons.filter(c => c.status === 'completed').length > 0 && (
                      <Button 
                        variant="outline"
                        onClick={handleExportAllComparisons}
                        className="gap-2"
                      >
                        <Download className="w-4 h-4" />
                        Export All ({previousComparisons.filter(c => c.status === 'completed').length})
                      </Button>
                    )}
                    <Button 
                      onClick={() => setActiveTab("upload")}
                      className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Compare Another Budget
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {previousComparisons.length > 0 ? (
                  <div className="space-y-4">
                    {previousComparisons.map((comp, index) => (
                      <div 
                        key={comp.id}
                        className={`p-4 rounded-lg transition-colors border-2 ${
                          comparison?.comparison_id === comp.id 
                            ? 'bg-emerald-50 border-emerald-300' 
                            : 'bg-slate-50 border-transparent hover:bg-slate-100'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="relative">
                              <FileSpreadsheet className="w-10 h-10 text-emerald-600" />
                              {index === 0 && (
                                <Badge className="absolute -top-2 -right-2 bg-amber-500 text-xs px-1">Latest</Badge>
                              )}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{comp.uploaded_filename}</p>
                              <p className="text-sm text-slate-500">
                                {new Date(comp.created_at).toLocaleDateString('en-AU', {
                                  day: 'numeric',
                                  month: 'short',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                              {comp.status === 'completed' && (
                                <Badge variant="outline" className="mt-1 text-emerald-600 border-emerald-300">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Completed
                                </Badge>
                              )}
                              {comp.status === 'processing' && (
                                <Badge variant="outline" className="mt-1 text-amber-600 border-amber-300">
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  Processing...
                                </Badge>
                              )}
                              {comp.status === 'error' && (
                                <Badge variant="outline" className="mt-1 text-red-600 border-red-300">
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Error
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {comp.status === 'completed' && (
                              <>
                                <Button
                                  variant={comparison?.comparison_id === comp.id ? "default" : "outline"}
                                  size="sm"
                                  onClick={() => loadComparison(comp.id)}
                                  className={comparison?.comparison_id === comp.id ? "bg-emerald-600" : ""}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  {comparison?.comparison_id === comp.id ? "Viewing" : "View"}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownloadComparison(comp.id)}
                                >
                                  <Download className="w-4 h-4 mr-1" />
                                  Excel
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteComparison(comp.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Notes Section */}
                        {comp.status === 'completed' && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            {editingNotes === comp.id ? (
                              <div className="space-y-2">
                                <Textarea
                                  value={notesText}
                                  onChange={(e) => setNotesText(e.target.value)}
                                  placeholder="Add notes about this comparison..."
                                  className="min-h-[80px] text-sm"
                                  maxLength={2000}
                                />
                                <div className="flex items-center justify-between">
                                  <span className="text-xs text-slate-400">{notesText.length}/2000</span>
                                  <div className="flex gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={handleCancelEditNotes}
                                    >
                                      <X className="w-3 h-3 mr-1" />
                                      Cancel
                                    </Button>
                                    <Button
                                      size="sm"
                                      onClick={() => handleSaveNotes(comp.id)}
                                      className="bg-emerald-600 hover:bg-emerald-700"
                                    >
                                      <Save className="w-3 h-3 mr-1" />
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-2">
                                <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                                {comp.notes ? (
                                  <div className="flex-1">
                                    <p className="text-sm text-slate-600 whitespace-pre-wrap">{comp.notes}</p>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleStartEditNotes(comp)}
                                      className="mt-1 h-6 px-2 text-xs text-slate-500 hover:text-slate-700"
                                    >
                                      <Pencil className="w-3 h-3 mr-1" />
                                      Edit
                                    </Button>
                                  </div>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleStartEditNotes(comp)}
                                    className="h-6 px-2 text-xs text-slate-500 hover:text-slate-700"
                                  >
                                    <Pencil className="w-3 h-3 mr-1" />
                                    Add notes
                                  </Button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-lg font-medium">No comparisons yet</p>
                    <p className="text-sm mb-4">Upload a budget to create your first comparison</p>
                    <Button 
                      onClick={() => setActiveTab("upload")}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Upload First Budget
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
